import { useLocation } from 'react-router-dom';
import { useOrganizationQuery } from '@/api/queries/organizations';
import type { ErrorResponse } from '@/api/handlers/responses';
import { getUser } from '@/utils';
import {
    decideOnboardingRoute,
    type OnboardingGateDecision,
} from './onboardingGateDecision';

export type UseOnboardingGateResult = {
    /** `null` until the organisation has been read. */
    decision: OnboardingGateDecision | null;
    /** A failed read with nothing cached to decide from. */
    error: ErrorResponse | null;
    onRetry: () => void;
};

/**
 * Reads the organisation (once per session; Finish and accounting start
 * invalidate it) and the session's role, and decides the current route with
 * `decideOnboardingRoute`.
 *
 * A failed background refetch keeps the last organisation: the gate decides
 * from what it has rather than taking down a screen that was already allowed.
 */
export const useOnboardingGate = (): UseOnboardingGateResult => {
    const { pathname } = useLocation();
    const query = useOrganizationQuery();
    const organisation = query.data ?? null;

    return {
        decision: organisation
            ? decideOnboardingRoute({
                  organisation,
                  role: getUser()?.role ?? null,
                  pathname,
              })
            : null,
        error: organisation ? null : (query.error ?? null),
        onRetry: () => {
            void query.refetch();
        },
    };
};
